// AppSumo license redemption survives the login/OAuth bounce.
//
// A buyer redeeming from AppSumo arrives at
//   /fly-app/lead-gen-tools?appsumo_license=<uuid>
// almost always LOGGED OUT. The auth flow bounces them through Google sign-in
// and strips the query param, so the redemption (AuthProvider) would never see
// it. We capture the license on first page load (main.tsx, before React renders
// / any redirect) and consume it after the buyer is authenticated.
//
// Storage: localStorage (NOT sessionStorage). sessionStorage is per browsing
// context and can be dropped across a fresh/incognito OAuth round-trip (the
// return can land in a context whose sessionStorage was reset), which silently
// lost the license. localStorage is per-origin and survives the whole redirect.
// A short TTL keeps a stale, unredeemed license from lingering forever.

const KEY = 'pending_appsumo_license';
const TTL_MS = 30 * 60 * 1000; // 30 min: long enough for a slow OAuth, short enough not to linger.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Stash the `appsumo_license` query param (if a valid UUID) before login can strip it. */
export function captureAppsumoLicenseFromUrl(search: string): void {
  try {
    const key = new URLSearchParams(search).get('appsumo_license');
    if (key && UUID_RE.test(key)) {
      localStorage.setItem(KEY, JSON.stringify({ key, at: Date.now() }));
    }
  } catch {
    // private-mode / quota — redemption just won't auto-fire; harmless.
  }
}

/** Read the stashed license (if present + not expired) without clearing it. */
export function peekAppsumoLicense(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { key?: string; at?: number };
    const fresh = typeof parsed.at === 'number' && (Date.now() - parsed.at) <= TTL_MS;
    if (parsed.key && UUID_RE.test(parsed.key) && fresh) return parsed.key;
    localStorage.removeItem(KEY);
    return null;
  } catch {
    return null;
  }
}

/** Clear the stash once the license has been successfully redeemed. */
export function consumeAppsumoLicense(): void {
  try { localStorage.removeItem(KEY); } catch { /* noop */ }
}
